import React from "react";
import { Link } from "react-router-dom";
import { FileText } from "lucide-react";
import DashboardCard from "../components/DashboardCard";
import DashboardCardSkeleton from "../components/DashboardCardSkeleton";
import DashboardEmptyState from "../components/DashboardEmptyState";
import DashboardPagination from "../components/DashboardPagination";
import DashboardPeriodFilter from "../components/DashboardPeriodFilter";
import PropertyIdFilter, {
  PropertyIdFilterOption,
} from "../components/PropertyIdFilter";
import {
  DashboardPeriodPreset,
  IOffer,
  IPaginationMeta,
  OfferStatusFilter,
} from "../types/ownerDashboard.types";
import { getDashboardPeriodLabel } from "../utils/getDashboardPeriodLabel";
import ProposalCard from "./ProposalCard";
import { RentedPropertyCatalog } from "../utils/buildRentedPropertyCatalog";

interface NewOffersProps {
  offersList: IOffer[];
  pagination: IPaginationMeta | null;
  periodPreset: DashboardPeriodPreset;
  statusFilter: OfferStatusFilter;
  selectedPropertyId: number | null;
  propertyOptions: PropertyIdFilterOption[];
  periodRange?: { initial_date: string; final_date: string } | null;
  isLoading?: boolean;
  smDown: boolean;
  lgDown?: boolean;
  onPeriodChange: (period: DashboardPeriodPreset) => void;
  onStatusFilterChange: (status: OfferStatusFilter) => void;
  onPropertyChange: (propertyId: number | null) => void;
  onPageChange: (page: number) => void;
  rentedPropertyCatalog?: RentedPropertyCatalog;
}

export default function NewOffers({
  offersList,
  pagination,
  periodPreset,
  statusFilter,
  selectedPropertyId,
  propertyOptions,
  periodRange,
  isLoading = false,
  smDown,
  lgDown = false,
  onPeriodChange,
  onStatusFilterChange,
  onPropertyChange,
  onPageChange,
  rentedPropertyCatalog,
}: NewOffersProps) {
  const padding = smDown ? "0.875rem" : lgDown ? "1rem" : "1.125rem";
  const periodLabel = getDashboardPeriodLabel(periodPreset, periodRange ?? null);
  const currentPage = pagination?.current_page ?? 1;
  const totalPages = pagination?.last_page ?? 1;
  const isPendingTab = statusFilter === "pending";

  if (isLoading) {
    return <DashboardCardSkeleton minHeight="min-h-[16rem]" lines={4} />;
  }

  return (
    <DashboardCard padding={padding} className="flex h-full min-h-0 flex-col">
      <div className="flex min-h-0 w-full flex-1 flex-col gap-3">
        <div className="shrink-0">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 shrink-0 text-[#6ea8ff]" />
            <h2 className="text-base font-bold text-[#f0f6fc] sm:text-lg">
              Propostas de Contrato
            </h2>
          </div>
          <p className="mt-1 text-xs text-[#8b949e] sm:text-sm">
            Propostas nos {periodLabel}
          </p>
        </div>

        <div className="flex w-full gap-2 rounded-2xl border border-white/[0.08] bg-[#0d1117]/60 p-1">
          <button
            type="button"
            onClick={() => onStatusFilterChange("pending")}
            className={`flex-1 rounded-xl px-3 py-2 text-xs font-semibold transition-colors sm:text-sm ${
              isPendingTab
                ? "bg-[#6ea8ff]/20 text-[#6ea8ff]"
                : "text-[#8b949e] hover:bg-white/5"
            }`}
          >
            Pendentes
          </button>
          <button
            type="button"
            onClick={() => onStatusFilterChange("accepted")}
            className={`flex-1 rounded-xl px-3 py-2 text-xs font-semibold transition-colors sm:text-sm ${
              !isPendingTab
                ? "bg-[#6ea8ff]/20 text-[#6ea8ff]"
                : "text-[#8b949e] hover:bg-white/5"
            }`}
          >
            Aceitas
          </button>
        </div>

        <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
          <DashboardPeriodFilter
            value={periodPreset}
            onChange={onPeriodChange}
            label="Período"
            compact
          />
          <PropertyIdFilter
            properties={propertyOptions}
            selectedPropertyId={selectedPropertyId}
            onSelectProperty={onPropertyChange}
          />
        </div>

        {offersList.length === 0 ? (
          <DashboardEmptyState
            align="center"
            icon={FileText}
            title={isPendingTab ? "Nenhuma proposta pendente" : "Nenhuma proposta aceita"}
            description={
              isPendingTab
                ? "Quando houver propostas em negociação, elas aparecerão aqui para você aceitar, recusar ou negociar."
                : "Propostas aceitas no período selecionado aparecerão aqui."
            }
            className="flex-1 justify-center py-6"
          />
        ) : (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto rounded-2xl border border-white/[0.06] bg-[#0d1117]/40 p-2 [-ms-overflow-style:none] [scrollbar-width:thin] [scrollbar-color:#6ea8ff40_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#6ea8ff]/30">
              <div className="flex flex-col gap-2.5 pr-1">
                {offersList.map((offer) => (
                  <ProposalCard
                    key={`${offer.date}-${offer.property_id}-${offer.tenant_id}`}
                    offer={offer}
                    rentedPropertyCatalog={rentedPropertyCatalog}
                  />
                ))}
              </div>
            </div>
            {totalPages > 1 && (
              <DashboardPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
              />
            )}
          </>
        )}

        <Link
          to="/proposals"
          className="shrink-0 text-center text-xs font-semibold text-[#6ea8ff] transition-colors hover:text-[#8bbcff]"
        >
          Ver todas as propostas
        </Link>
      </div>
    </DashboardCard>
  );
}
